import { of as observableOf, Observable, ReplaySubject, throwError } from 'rxjs';

import { timeout, refCount, publishReplay, catchError, map, mergeMap, retryWhen, take, delay } from 'rxjs/operators';

import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Server } from './server';
import { User } from './user';
import { Token } from './token';

export interface IUserService {
  getUser(): Observable<User | null>;
  getServerDetail(): Observable<Server>;
  startServer(data: StartServerData): Observable<Token>;
  stopServer(): Observable<number>;
  reconnect(): Observable<Token>;
}

export interface StartServerData {
  id: number;
}

@Injectable()
export class UserService implements IUserService {
  readonly baseUrl = '/api/users/current/';
  readonly serverTimeoutMs = 1000;
  readonly retryDelayMs: number = 100;
  readonly maxRetries: number = 5;

  userReplay: ReplaySubject<User | null>;
  user$: Observable<User | null>;

  constructor(private _http: HttpClient) {}

  getUser(): Observable<User | null> {
    if (this.user$ === undefined) {
      this.userReplay = new ReplaySubject<User | null>();

      this._http
        .get<User>(this.baseUrl, { observe: 'response' })
        .pipe(
          map(response => (response.status < 300 ? response.body : null)),
          catchError(() => observableOf(null))
        )
        .subscribe(u => this.userReplay.next(u));

      this.user$ = this.userReplay.pipe(publishReplay(1), refCount());
    }

    return this.user$;
  }

  /* Returns a nullish server-object on error */
  getServerDetail(): Observable<Server> {
    console.log('get server detail');
    return this._http.get<Server>(this.baseUrl + 'server/').pipe(
      timeout(this.serverTimeoutMs),
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, index) => {
            const attemptNumber = index + 1;
            if (attemptNumber > this.maxRetries) {
              return throwError(() => new Error('Failed after ' + this.maxRetries + ' retries'));
            }
            console.log('Attempt ' + attemptNumber + ': retrying in ' + this.retryDelayMs + 'ms');
            return observableOf(error).pipe(delay(this.retryDelayMs));
          }),
          take(this.maxRetries)
        )
      ),
      catchError(() =>
        observableOf({
          status: null,
          latest_container: null,
        })
      )
    );
  }

  /* The error for this is caught in the container-selector component */
  startServer(data: StartServerData): Observable<Token> {
    const postData = data !== null ? data : {};
    return this._http.post<Token>(this.baseUrl + 'server/', postData).pipe(timeout(this.serverTimeoutMs));
  }

  stopServer(): Observable<number> {
    return this._http.delete(this.baseUrl + 'server/', { observe: 'response' }).pipe(
      map(response => response.status),
      timeout(this.serverTimeoutMs)
    );
  }

  reconnect(): Observable<Token> {
    return this._http.get<Token>(this.baseUrl + 'token/').pipe(timeout(this.serverTimeoutMs));
  }

  testNotebookStatus(url: string): Observable<HttpResponse<string>> {
    return this._http.get(url, { observe: 'response', responseType: 'text' }).pipe(timeout(this.serverTimeoutMs));
  }
}
